/**
 * TextEditSettings.tsx
 * TextEditカードの設定ダイアログの中身（文字入力）
 */
import { useReactFlow } from '@xyflow/react';
import { useEffect, useRef, useState } from 'react';
import type { NodeSettingsProps } from '../../types';
import { useFlowDisplayLanguage } from '../../displayLanguage';
import { OFFLINE_MODE } from '@/config/offline';
import { OFFLINE_PHRASES } from '@/speech/offlineSpeech';

export function TextEditSettings({ node }: NodeSettingsProps<'textEdit'>) {
  const { updateNodeData } = useReactFlow();
  const displayLanguage = useFlowDisplayLanguage();
  const [draft, setDraft] = useState(node.data.text ?? '');
  const composingRef = useRef(false);

  useEffect(() => {
    if (!composingRef.current) setDraft(node.data.text ?? '');
  }, [node.data.text]);

  const commit = (value: string) => {
    const text = value.replace(/\n/g, '');
    setDraft(text);
    updateNodeData(node.id, { text });
  };

  if (OFFLINE_MODE) {
    const known = OFFLINE_PHRASES.some(({ text }) => text === draft);
    return (
      <label className="block" data-product-tour="card-input-1">
        <span className="mb-1 block text-sm font-medium text-slate-700">事前作成した文章</span>
        <select
          value={known ? draft : ''}
          onChange={(event) => commit(event.target.value)}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
        >
          <option value="" disabled>文章を選択してください</option>
          {OFFLINE_PHRASES.map(({ id, text }) => <option key={id} value={text}>{text}</option>)}
        </select>
        {!known && <p role="status" className="mt-2 text-sm text-amber-800">既存の自由文はそのまま保持しています。実行前に定型文を選び直してください。</p>}
      </label>
    );
  }

  return (
    <label className="block" data-product-tour="card-input-1">
      <span className="mb-1 block text-sm font-medium text-slate-700">
        {displayLanguage === 'en' ? 'text' : '文字'}
      </span>
      <textarea
        value={draft}
        maxLength={200}
        placeholder={displayLanguage === 'en' ? 'Enter text...' : '文字を入力...'}
        onCompositionStart={() => { composingRef.current = true; }}
        onCompositionEnd={(event) => {
          composingRef.current = false;
          commit(event.currentTarget.value);
        }}
        onChange={(event) => {
          const text = event.target.value.replace(/\n/g, '');
          setDraft(text);
          if (!composingRef.current) updateNodeData(node.id, { text });
        }}
        rows={4}
        className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-emerald-400"
      />
    </label>
  );
}
